import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ipoService } from '../../api/ipoService';

// The endpoint returns its 10 most subscribed issues by default; the page
// ranks and filters the whole set client-side
const ROW_LIMIT = 50;

/**
 * Async thunk for the Top Subscribed IPOs page (/top-subscribed-ipos)
 * GET /api/market/subscription/top?limit=50
 *
 * options.maxAgeMs: skip the request when data this recent is already loaded
 */
export const fetchTopSubscribed = createAsyncThunk(
  'topSubscription/fetchTopSubscribed',
  async (_options, { rejectWithValue }) => {
    try {
      const response = await ipoService.getTopSubscription({ limit: ROW_LIMIT });
      if (response?.success === false) {
        return rejectWithValue(response.error || 'Failed to fetch the most subscribed IPOs');
      }
      const data = response?.data || {};
      return {
        items: Array.isArray(data.items) ? data.items : [],
        total: typeof data.total === 'number' ? data.total : (data.items || []).length,
        refreshedAt: data.refreshed_at || null,
        note: data.error || null,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch the most subscribed IPOs');
    }
  },
  {
    condition: ({ maxAgeMs = 0 } = {}, { getState }) => {
      const { status, fetchedAt } = getState().topSubscription;
      // Skip duplicate requests while one is in flight (e.g. StrictMode double effects)
      if (status === 'loading') return false;
      return !(maxAgeMs && status === 'succeeded' && fetchedAt && Date.now() - fetchedAt < maxAgeMs);
    },
  }
);

const initialState = {
  // Raw items; the page maps them with utils/topSubscribed.js
  items: [],
  total: 0,
  refreshedAt: null,
  // The API's soft message when the ranking is empty or partial
  note: null,
  fetchedAt: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

export const topSubscriptionSlice = createSlice({
  name: 'topSubscription',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTopSubscribed.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchTopSubscribed.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.items;
        state.total = action.payload.total;
        state.refreshedAt = action.payload.refreshedAt;
        state.note = action.payload.note;
        state.fetchedAt = Date.now();
      })
      .addCase(fetchTopSubscribed.rejected, (state, action) => {
        // Keep the previous ranking so a failed refresh doesn't blank the page
        state.status = 'failed';
        state.error = action.payload || 'Failed to load the most subscribed IPOs';
      });
  },
});

export default topSubscriptionSlice.reducer;
